import React, { useState, useRef } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Upload, X, Image } from 'lucide-react';
import { customBackgroundImageAtom, clockModeThemeAtom } from '../../../../../atoms/v3/clockModeAtoms';
import {
  initUppyAtom,
  enqueueFilesAtom,
  fileValuesSelector,
  removeSingleFileAtom,
  uppyInstanceAtom
} from '../../../../../atoms/v3/uppyAtoms';

interface CustomBackgroundUploaderProps {
  onUploadComplete?: () => void;
}

export const CustomBackgroundUploader: React.FC<CustomBackgroundUploaderProps> = ({
  onUploadComplete
}) => {
  const { t } = useTranslation(['v3/clock']);
  const [customBackgroundImage, setCustomBackgroundImage] = useAtom(customBackgroundImageAtom);
  const setClockTheme = useSetAtom(clockModeThemeAtom);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Uppy atoms
  const initUppy = useSetAtom(initUppyAtom);
  const enqueueFiles = useSetAtom(enqueueFilesAtom);
  const [uploadedFiles] = useAtom(fileValuesSelector);
  const [, removeSingleFile] = useAtom(removeSingleFileAtom);
  const [uppyInstance] = useAtom(uppyInstanceAtom);

  // 初始化 Uppy
  React.useEffect(() => {
    initUppy({ autoProceed: true, maxNumberOfFiles: 1 });
  }, [initUppy]);

  // 监听文件上传完成
  React.useEffect(() => {
    const completedFiles = uploadedFiles.filter(file =>
      file.uploadStatus === 'success' && file.finalUrl && file.type.startsWith('image/')
    );

    if (completedFiles.length > 0) {
      const latestFile = completedFiles[completedFiles.length - 1];
      if (latestFile.finalUrl && latestFile.finalUrl !== customBackgroundImage) {
        setCustomBackgroundImage(latestFile.finalUrl);
        setClockTheme('custom-image');
        setUploadError(null);
        onUploadComplete?.();

        // 清理已完成的文件：同时从 Uppy 实例和状态中移除
        if (uppyInstance) {
          // 清理 Uppy 实例中的所有文件
          uppyInstance.cancelAll();
          const files = uppyInstance.getFiles();
          files.forEach(file => {
            uppyInstance.removeFile(file.id);
          });
        }
        // 清理状态中的文件
        removeSingleFile(latestFile.id);
      }
    }

    // 检查是否有上传失败的文件
    const failedFiles = uploadedFiles.filter(file =>
      file.uploadStatus === 'error' && file.type.startsWith('image/')
    );

    if (failedFiles.length > 0) {
      setUploadError('上传失败，请重试');

      // 清理失败的文件：同时从 Uppy 实例和状态中移除
      if (uppyInstance) {
        failedFiles.forEach(file => {
          uppyInstance.removeFile(file.id);
        });
      }
      failedFiles.forEach(file => removeSingleFile(file.id));
    }
  }, [uploadedFiles, customBackgroundImage, setCustomBackgroundImage, setClockTheme, onUploadComplete, removeSingleFile]);

  const validateFile = (file: File): string | null => {
    // 检查文件类型
    if (!file.type.startsWith('image/')) {
      return '请选择图片文件';
    }
    
    // 检查文件大小 (10MB 限制)
    if (file.size > 10 * 1024 * 1024) {
      return '图片文件不能超过10MB';
    }
    
    return null;
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    const file = files[0];
    const error = validateFile(file);
    
    if (error) {
      setUploadError(error);
      return;
    }

    setUploadError(null);
    
    // 添加文件到上传队列（autoProceed: true 会自动开始上传）
    enqueueFiles([{
      name: file.name,
      type: file.type,
      data: file,
      size: file.size,
    }]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = () => {
    setCustomBackgroundImage('');
    setUploadError(null);

    // 清理 Uppy 实例中的所有文件
    if (uppyInstance) {
      uppyInstance.cancelAll();
      const files = uppyInstance.getFiles();
      files.forEach(file => {
        uppyInstance.removeFile(file.id);
      });
    }
  };

  // 获取当前上传中的背景图片文件
  const uploadingBackgroundFile = uploadedFiles.find(file =>
    file.type.startsWith('image/') &&
    ['preparing', 'uploading'].includes(file.uploadStatus)
  );

  // 检查是否有文件正在上传
  const isUploading = !!uploadingBackgroundFile;

  return (
    <div className="space-y-4">
      {/* 当前自定义图片预览 */}
      {customBackgroundImage && (
        <div className="relative">
          <div className="text-sm font-medium mb-2">当前自定义背景</div>
          <div className="relative w-full h-32 rounded-lg overflow-hidden border border-gray-200">
            <img
              src={customBackgroundImage}
              alt="自定义背景"
              className="w-full h-full object-cover"
            />
            <button
              onClick={handleRemoveImage}
              className="absolute top-2 right-2 p-1 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-all"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* 上传区域 */}
      <div>
        <div className="text-sm font-medium mb-2">
          {customBackgroundImage ? '替换背景图片' : '上传背景图片'}
        </div>
        
        <div
          className={`
            relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
            ${isDragOver 
              ? 'border-blue-400 bg-blue-50' 
              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
            }
            ${isUploading ? 'pointer-events-none opacity-50' : ''}
          `}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
          
          <div className="flex flex-col items-center space-y-2">
            {isUploading ? (
              <>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="text-sm text-gray-600">上传中...</span>
              </>
            ) : (
              <>
                <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full">
                  {isDragOver ? <Image size={24} className="text-blue-500" /> : <Upload size={24} className="text-gray-400" />}
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium text-blue-600">点击上传</span> 或拖拽图片到此处
                </div>
                <div className="text-xs text-gray-400">
                  支持 JPG、PNG、GIF 格式，最大 10MB
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 错误提示 */}
      {uploadError && (
        <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
          {uploadError}
        </div>
      )}
    </div>
  );
};

export default CustomBackgroundUploader;
